import { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

const AccountContext = createContext({});

export function AccountProvider({ children }) {
  const { user } = useAuth();
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.email) {
      fetchAccountData();
    } else {
      setAccountData(null);
      setLoading(false);
    }
  }, [user?.email]);

  const fetchAccountData = async () => {
    if (!user?.email) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .eq("email", user.email)
        .maybeSingle();

      if (data && !error) {
        setAccountData(data);
      } else {
        setAccountData(null);
      }
    } catch (err) {
      console.error("Error fetching account:", err);
      setAccountData(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshAccountData = () => {
    return fetchAccountData();
  };

  const value = {
    accountData,
    loading,
    refreshAccountData,
    oxpayUserId: accountData?.oxpay_user_id || null,
    walletId: accountData?.wallet_id || null,
    kycStatus: accountData?.kyc_status || null,
    poaStatus: accountData?.poa_status || null,
    oxpayStatus: accountData?.oxpay_user_id
      ? accountData?.oxpay_status || "active"
      : "not_created",
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error("useAccount must be used within an AccountProvider");
  }
  return context;
}
